/**
 * Schémas Zod du module Intrants œnologiques (chantier Lot I) — partagés API ⇄ ERP.
 *
 * Deux objets distincts :
 *  - `intrants_oenologiques` : RÉFÉRENTIEL tenant des produits (mutable, soft
 *    delete via `actif`). Porte les drapeaux allergène (INCO) et « pratique
 *    déclarable » (2018/273 art. 29 §2) — déduits du produit, jamais demandés
 *    à la saisie d'un ajout.
 *  - `ajouts_intrants` : JOURNAL append-only des ajouts (sulfitage, levurage,
 *    collage…). Cible = vin clair OU cuve seule (moût au débourbage — décision
 *    D4 du cadrage). Alimente le registre des manipulations via la vue
 *    `registre_cave` v2 (branche 7, migration 0084). Correction = ligne inverse
 *    portant `annule_ajout_id` — jamais d'UPDATE (règle projet n°4).
 *
 * Cf `docs/features/lot-i-cave-conformite/{01-metier,03-modele}.md` et migration 0079.
 */

import { z } from "zod";

import { idSchema } from "./parcellaire.js";
import { dateIsoSchema } from "./vendange.js";

// ────────────────────────────────────────────────────────────────────────────
// Énumérations (miroirs des CHECK SQL — migration 0079)
// ────────────────────────────────────────────────────────────────────────────

/** Type d'intrant — miroir CHECK SQL `intrants_oenologiques.type_intrant`. */
export const typeIntrantSchema = z.enum([
  "so2",
  "levure",
  "nutriment",
  "enzyme",
  "bentonite",
  "colle",
  "lysozyme",
  "acidifiant",
  "desacidifiant",
  "stabilisant",
  "liqueur",
  "adjuvant_tirage",
  "autre",
]);
export type TypeIntrant = z.infer<typeof typeIntrantSchema>;

/**
 * Libellés métier des types d'intrants (vocabulaire cave, pas de jargon) —
 * partagés API (registre de cave) ⇄ ERP (grille de saisie, journal) — DRY.
 */
export const TYPE_INTRANT_LABELS: Record<TypeIntrant, string> = {
  so2: "SO2 / sulfitage",
  levure: "Levures (LSA)",
  nutriment: "Nutriment azoté",
  enzyme: "Enzymes",
  bentonite: "Bentonite",
  colle: "Colle",
  lysozyme: "Lysozyme",
  acidifiant: "Acidifiant",
  desacidifiant: "Désacidifiant",
  stabilisant: "Stabilisant",
  liqueur: "Liqueur",
  adjuvant_tirage: "Adjuvant de tirage",
  autre: "Autre",
};

/**
 * Allergène à mentionner (règlements INCO 1169/2011 + 2019/33). L'ichtyocolle
 * (poisson) est EXEMPTÉE d'étiquetage → elle se modélise `est_allergene=false`.
 */
export const typeAllergeneSchema = z.enum(["sulfites", "oeuf", "lait"]);
export type TypeAllergene = z.infer<typeof typeAllergeneSchema>;

/** Unités de dose — miroir CHECK SQL `ajouts_intrants.unite`. */
export const uniteIntrantSchema = z.enum([
  "g_hl",
  "g_l",
  "mg_l",
  "ml_hl",
  "l_hl",
  "kg",
  "g",
  "l",
  "ml",
]);
export type UniteIntrant = z.infer<typeof uniteIntrantSchema>;

/** Libellés d'affichage des unités (partagés API/ERP — DRY). */
export const UNITE_INTRANT_LABELS: Record<UniteIntrant, string> = {
  g_hl: "g/hL",
  g_l: "g/L",
  mg_l: "mg/L",
  ml_hl: "mL/hL",
  l_hl: "L/hL",
  kg: "kg",
  g: "g",
  l: "L",
  ml: "mL",
};

/**
 * Cible d'un ajout d'intrant (décision D4) :
 *  - `vin_clair` : l'ajout suit le LOT (la cuve est snapshotée côté serveur).
 *  - `cuve`      : moût avant création du vin clair (débourbage) — cuve seule.
 */
export const ajoutIntrantCibleSchema = z.enum(["vin_clair", "cuve"]);
export type AjoutIntrantCible = z.infer<typeof ajoutIntrantCibleSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Schémas atomiques partagés
// ────────────────────────────────────────────────────────────────────────────

/** Notes libres optionnelles — vide normalisé en undefined. */
const notesSchema = z
  .string()
  .trim()
  .max(4000, "Notes trop longues (4000 caractères max).")
  .optional()
  .or(z.literal("").transform(() => undefined));

/**
 * N° de lot fournisseur (traçabilité 178/2002 art. 18), optionnel. Le `.min(1)`
 * force la chaîne vide à basculer sur la branche `literal("") → undefined`.
 */
const numeroLotFournisseurSchema = z
  .string()
  .trim()
  .min(1)
  .max(64, "N° de lot trop long (64 caractères max).")
  .optional()
  .or(z.literal("").transform(() => undefined));

/** Dose strictement positive, bornée large (garde-fou de saisie, pas métier). */
export const doseIntrantSchema = z
  .number({ invalid_type_error: "Dose invalide.", required_error: "Dose requise." })
  .positive("La dose doit être supérieure à 0.")
  .max(1_000_000, "Dose invraisemblable (vérifiez l'unité).");

// ────────────────────────────────────────────────────────────────────────────
// Référentiel produits — inputs
// ────────────────────────────────────────────────────────────────────────────

/** Champs communs création/modification d'un produit du référentiel. */
const intrantShape = {
  /** Nom commercial du produit (ex. « Sulfistop 10 % »). Unique par domaine. */
  nom: z
    .string()
    .trim()
    .min(1, "Nom du produit requis.")
    .max(120, "Nom trop long (120 caractères max)."),
  type_intrant: typeIntrantSchema,
  /**
   * Allergène du produit (INCO) — `undefined` = non allergène. Le drapeau
   * `est_allergene` DB est dérivé de la présence de cette valeur (CHECK croisé).
   */
  type_allergene: typeAllergeneSchema.optional(),
  /** Pratique à mentions renforcées au registre (2018/273 art. 29 §2). */
  est_pratique_declarable: z.boolean().optional(),
  /** Unité proposée par défaut à la saisie d'un ajout. */
  unite_par_defaut: uniteIntrantSchema.optional(),
  fournisseur: z
    .string()
    .trim()
    .max(120, "Fournisseur trop long (120 caractères max).")
    .optional()
    .or(z.literal("").transform(() => undefined)),
  notes: notesSchema,
} as const;

/** Input `intrants.creerProduit` — nouveau produit au référentiel tenant. */
export const intrantCreerInput = z.object(intrantShape);
export type IntrantCreerInput = z.infer<typeof intrantCreerInput>;

/**
 * Input `intrants.modifierProduit` — référentiel MUTABLE (seul cas d'UPDATE du
 * module). `actif=false` = soft delete (masqué à la saisie, historique intact).
 */
export const intrantModifierInput = z.object({
  id: idSchema,
  ...intrantShape,
  actif: z.boolean().optional(),
});
export type IntrantModifierInput = z.infer<typeof intrantModifierInput>;

/** Input `intrants.listerProduits` — filtre `actif` optionnel (défaut : tous). */
export const intrantListerInput = z
  .object({
    actif: z.boolean().optional(),
  })
  .optional();
export type IntrantListerInput = z.infer<typeof intrantListerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Journal des ajouts — inputs
// ────────────────────────────────────────────────────────────────────────────

/**
 * Input `intrants.creerAjout` — enregistrer UN ajout d'intrant (append-only).
 *
 * `cible=vin_clair` : le serveur snapshote la cuve courante du lot.
 * `cible=cuve`      : ajout sur moût (cuve seule, avant vin clair).
 * `date_ajout` = date MÉTIER terrain (inscription jour même / jour ouvrable
 * suivant — 2018/274 art. 20), pas la date de sync.
 */
export const ajoutIntrantCreerInput = z.object({
  intrant_id: idSchema,
  cible: ajoutIntrantCibleSchema,
  /** ID de la cible (vin clair ou cuve) — validé/scopé tenant côté serveur. */
  cible_id: idSchema,
  date_ajout: dateIsoSchema,
  dose: doseIntrantSchema,
  unite: uniteIntrantSchema,
  numero_lot_fournisseur: numeroLotFournisseurSchema,
  /** Opérateur (défaut : utilisateur de session) — membre actif vérifié serveur. */
  operateur_id: idSchema.optional(),
  notes: notesSchema,
});
export type AjoutIntrantCreerInput = z.infer<typeof ajoutIntrantCreerInput>;

/** Input `intrants.listerAjouts` — journal d'UNE cible (vin clair ou cuve). */
export const ajoutIntrantListerInput = z.object({
  cible: ajoutIntrantCibleSchema,
  cible_id: idSchema,
});
export type AjoutIntrantListerInput = z.infer<typeof ajoutIntrantListerInput>;

/**
 * Input `intrants.annulerAjout` — contre-passation (ligne inverse portant
 * `annule_ajout_id`, mêmes valeurs). Jamais d'UPDATE/DELETE de l'original.
 */
export const ajoutIntrantAnnulerInput = z.object({
  ajout_id: idSchema,
  motif: z
    .string()
    .trim()
    .max(500, "Motif trop long (500 caractères max).")
    .optional()
    .or(z.literal("").transform(() => undefined)),
});
export type AjoutIntrantAnnulerInput = z.infer<typeof ajoutIntrantAnnulerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Sorties (validation Zod en sortie — defense in depth)
// ────────────────────────────────────────────────────────────────────────────

/** Produit du référentiel tenant. */
export const intrantSortieSchema = z.object({
  id: idSchema,
  nom: z.string(),
  type_intrant: typeIntrantSchema,
  est_allergene: z.boolean(),
  type_allergene: typeAllergeneSchema.nullable(),
  est_pratique_declarable: z.boolean(),
  unite_par_defaut: uniteIntrantSchema.nullable(),
  fournisseur: z.string().nullable(),
  actif: z.boolean(),
  notes: z.string().nullable(),
  cree_le: z.date(),
});
export type IntrantSortie = z.infer<typeof intrantSortieSchema>;

export const intrantListeSortieSchema = z.array(intrantSortieSchema);

/**
 * Ligne du journal des ajouts. La dose est un NUMERIC Postgres → `string`
 * (précision préservée). Les drapeaux allergène/déclarable viennent du produit
 * (jointure) pour les badges du journal.
 */
export const ajoutIntrantSortieSchema = z.object({
  id: idSchema,
  intrant_id: idSchema,
  intrant_nom: z.string(),
  type_intrant: typeIntrantSchema,
  est_allergene: z.boolean(),
  type_allergene: typeAllergeneSchema.nullable(),
  est_pratique_declarable: z.boolean(),
  cible: ajoutIntrantCibleSchema,
  vin_clair_id: idSchema.nullable(),
  /** Cuve : cible réelle (moût) ou simple snapshot (cible vin clair). */
  cuve_id: idSchema.nullable(),
  date_ajout: z.string(),
  dose: z.string(),
  unite: uniteIntrantSchema,
  numero_lot_fournisseur: z.string().nullable(),
  /** Renseigné si CETTE ligne est une contre-passation (elle annule une autre). */
  annule_ajout_id: idSchema.nullable(),
  /** Vrai si cette ligne a été annulée par une ligne inverse (paire à exclure). */
  est_annule: z.boolean(),
  notes: z.string().nullable(),
  cree_le: z.date(),
});
export type AjoutIntrantSortie = z.infer<typeof ajoutIntrantSortieSchema>;

export const ajoutIntrantListeSortieSchema = z.array(ajoutIntrantSortieSchema);

/**
 * Sortie `intrants.creerAjout`. `allergene_ajoute` alimente l'encart informatif
 * UI (« ce collage ajoutera “contient : lait” aux étiquettes… »).
 */
export const ajoutIntrantCreerSortieSchema = z.object({
  id: idSchema,
  cree_le: z.date(),
  allergene_ajoute: typeAllergeneSchema.nullable(),
  est_pratique_declarable: z.boolean(),
});
export type AjoutIntrantCreerSortie = z.infer<typeof ajoutIntrantCreerSortieSchema>;

/** Sortie `intrants.annulerAjout`. */
export const ajoutIntrantAnnulerSortieSchema = z.object({
  id: idSchema,
  annule_ajout_id: idSchema,
  cree_le: z.date(),
});
export type AjoutIntrantAnnulerSortie = z.infer<typeof ajoutIntrantAnnulerSortieSchema>;

/**
 * Produit « récent » pour la saisie en 3 gestes (grille des derniers produits
 * utilisés, dose/unité/n° lot pré-remplis du dernier usage — UX É2).
 */
export const intrantRecentSortieSchema = z.object({
  intrant_id: idSchema,
  nom: z.string(),
  type_intrant: typeIntrantSchema,
  est_allergene: z.boolean(),
  type_allergene: typeAllergeneSchema.nullable(),
  derniere_dose: z.string(),
  derniere_unite: uniteIntrantSchema,
  dernier_numero_lot: z.string().nullable(),
});
export type IntrantRecentSortie = z.infer<typeof intrantRecentSortieSchema>;

export const intrantsRecentsSortieSchema = z.array(intrantRecentSortieSchema);

/** Nombre de produits proposés dans la grille « récents » (UX É2 : 6). */
export const NB_INTRANTS_RECENTS = 6;
